import defaultErrorHandler, { IMensagemErro } from '../ErrorHandling';
import UsuarioStore from '../store/UsuarioStore';
import { AppStore } from "../store/AppStore";
import MensagemStore from "../store/MensagemStore";
import Ambiente from "../utils/Ambiente";
import Configuracao from "../utils/Configuracao";
import { apresentarApiErroPadrao, marcarApiErroPadraoExibido } from '@/utils/ApiErroPadraoUtils';

import axios from 'axios';
import axiosRetry from 'axios-retry';

class AxiosConfig {
    private jaConfigurado = false;

    configurarAxios() {
        if (this.jaConfigurado) {
            return;
        }

        axiosRetry(axios, { retries: 0 });
        axios.defaults.timeout = Configuracao.DEFAULT_AXIOS_TIMEOUT;
        axios.defaults.headers.common['Accept'] = '*/*';
        axios.interceptors.request.use(this.requestInterceptorHandler, this.requestInterceptorErrorHandler);
        axios.interceptors.response.use(this.responseInterceptorHandler, this.responseInterceptorErrorHandler);
        this.jaConfigurado = true;
    }

    requestInterceptorErrorHandler = (error) => {
        AppStore.zerarRequisicoes();
        return Promise.reject(error);
    }

    requestInterceptorHandler = async (config) => {
        if ((config as any).skipAuthentication) {
            return config;
        }

        if (config.headers.Authorization) {
            AppStore.incrementarRequisicoes();
        } else {
            config.headers.Authorization = 'Bearer ' + await UsuarioStore.configToken();
        }
        return config;
    }

    responseInterceptorHandler = (response) => {
        if (response) {
            AppStore.decrementarRequisicoes();
            Ambiente.removerRequisicao(response.config.url);
            if (!response?.config?.silent) {
                this.tratarMensagensErroStatus200(response);
            }
        }
        return response;
    }

    tratarMensagensErroStatus200(response) {
        if (apresentarApiErroPadrao(response?.data, { exigirAssinaturaDeErro: true })) {
            return;
        }

        let mensagemRetorno;
        if (Array.isArray(response.data)) {
            const erros = response.data.filter(erro => erro?.tipo === 'ERRO');
            const mensagens = erros.map(erro => erro.mensagens);
            mensagemRetorno = mensagens?.length > 0 ? mensagens : undefined;
            response.data['erro'] = mensagemRetorno !== undefined;
        } else if (response?.data?.tipo === 'ERRO') {
            mensagemRetorno = response.data.mensagens;
            response.data.erro = mensagemRetorno !== undefined;
        }
        if (mensagemRetorno) {
            MensagemStore.apresentarErro(mensagemRetorno);
        }
    }

    responseInterceptorErrorHandler = (error) => {
        const isSilent = error?.config?.silent;

        if (!axios.isCancel(error)) {
            if (isSilent) {
                AppStore.zerarRequisicoes();
                return Promise.reject(error);
            }

            if (apresentarApiErroPadrao(error) || apresentarApiErroPadrao(error?.response?.data)) {
                AppStore.zerarRequisicoes();
                return Promise.reject(error);
            }

            defaultErrorHandler({
                unauthorized: () => {
                    AppStore.fecharApp();
                    console.warn(error.response?.data);
                    MensagemStore.apresentarErro('Login inválido');
                },
                erroTratado: (error: any, resposta: IMensagemErro) => {
                    console.warn(resposta?.mensagem, error);

                    if (apresentarApiErroPadrao(error) || apresentarApiErroPadrao(error?.response?.data) || apresentarApiErroPadrao(resposta?.mensagem)) {
                        return;
                    }

                    if (resposta?.variante === 'warning') {
                        MensagemStore.apresentarAviso(resposta.mensagem);
                        marcarApiErroPadraoExibido(error);
                        return;
                    } else if (resposta?.variante === 'error') {
                        MensagemStore.apresentarErro(resposta.mensagem);
                        marcarApiErroPadraoExibido(error);
                        return;
                    }

                    let erroJson: any = null;

                    try {
                        erroJson = typeof resposta?.mensagem === 'string' && (resposta.mensagem.trim().startsWith('{') || resposta.mensagem.trim().startsWith('['))
                            ? JSON.parse(resposta.mensagem)
                            : (typeof resposta?.mensagem === 'object' ? resposta?.mensagem : error.response?.data);

                        if (erroJson?.reason) {
                            let mensagem = erroJson.reason.replace("Forbidden", "Não autorizado.");
                            const tentativaDeEnvioAPauta = error?.config?.url?.includes("enviarParaPauta");

                            if (tentativaDeEnvioAPauta && error?.response?.status === 500 && mensagem?.includes("Não autorizado")) {
                                mensagem += " Favor verificar se o usuário logado tem o mesmo CPF do magistrado cadastrado no sistema de origem.";
                            } else if (tentativaDeEnvioAPauta) {
                                mensagem = mensagem.replace(
                                    "Processo não está com andamento de Visto a Pauta",
                                    "O processo não foi remetido ao gabinete com andamento compatível com visto a pauta."
                                );
                            }

                            MensagemStore.apresentarErro(mensagem);
                        } else if (erroJson) {
                            const mensagemExtraida = typeof erroJson === 'string'
                                ? erroJson
                                : (erroJson.mensagem || erroJson.message || erroJson.detalhe || erroJson.details || (typeof erroJson.error === 'string' && erroJson.error !== 'Not Found' && erroJson.error !== 'Internal Server Error' ? erroJson.error : undefined));

                            if (typeof mensagemExtraida === 'string' && mensagemExtraida.trim() && mensagemExtraida.trim().toLowerCase() !== 'no message available') {
                                MensagemStore.apresentarErro(mensagemExtraida.trim());
                            } else if (error?.response?.status === 404 || error?.config?.url?.includes('/processos/')) {
                                MensagemStore.apresentarAviso('Processo não encontrado.');
                            } else if (error?.response?.status === 500) {
                                MensagemStore.apresentarErro('Erro inesperado no servidor');
                            } else {
                                MensagemStore.apresentarErro('Ocorreu um erro no processamento da solicitação.');
                            }
                        }
                    } catch (_e) {
                        // Ignora erro de parsing
                    }

                    if (!erroJson) {
                        let mensagem = resposta?.mensagem;
                        if ((!mensagem && error.request?.status >= 500 && error.request?.status <= 599) || mensagem?.includes('Request failed with status code 500')) {
                            mensagem = 'Erro inesperado no servidor';
                        }
                        if (mensagem) {
                            MensagemStore.apresentarErro(mensagem);
                        }
                    }
                }
            })(error);

            AppStore.zerarRequisicoes();
            return Promise.reject(error);
        }

        AppStore.zerarRequisicoes();
        return Promise.reject(error);
    };

    private formataRespostaParaErroEnvioPauta(erroJson: string) {
        const corpo: string = (erroJson as any).reason;
        const retorno = corpo.split('.');
        const mensagemFormatada: string = retorno[0] + '. ' + retorno[1] + '.' + '<br/><br/>' + retorno[2] + '<br/>' + retorno[3] + '<br/>' + retorno[4] + '<br/>' + retorno[5];
        return mensagemFormatada;
    }

    setAuthorization(token: string) {
        axios.defaults.headers.common['Authorization'] = token;
    }
}

export default new AxiosConfig();
